import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import type { HotelPriceTier } from "@/lib/daycare-hotel/hotelPriceTier";
import { cn } from "@/lib/utils";

export interface HotelRatePanelProps {
  standard: number | null;
  /** Nulo = a clínica não diferencia; todo mundo paga a normal. */
  student: number | null;
  applied: HotelPriceTier;
  petName: string;
  locale: string;
}

/**
 * As duas faixas do hotel lado a lado, com a aplicada em destaque. Ver o preço
 * que ficou de fora é o que explica a conta para o responsável.
 */
export function HotelRatePanel({ standard, student, applied, petName, locale }: HotelRatePanelProps) {
  const { t } = useTranslation("daycareHotel");
  const format = (value: number | null) =>
    value === null
      ? t("dailyRates.notConfigured")
      : new Intl.NumberFormat(locale, { style: "currency", currency: "BRL" }).format(value);

  const tier = (value: HotelPriceTier, amount: number | null) => {
    const isApplied = applied === value;
    return (
      <div
        key={value}
        data-tier={value}
        data-applied={isApplied || undefined}
        className={cn(
          "rounded-xl border px-3 py-2",
          isApplied ? "border-primary bg-primary/10" : "border-border bg-card opacity-60",
        )}
      >
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          {t(`priceTier.${value}`)}
          {isApplied ? <Check aria-hidden="true" size={14} className="text-primary" /> : null}
        </span>
        <span className="mt-0.5 block font-display text-base font-bold text-foreground">{format(amount)}</span>
      </div>
    );
  };

  return (
    <section role="status" className="grid gap-2">
      <div className="grid gap-2 sm:grid-cols-2">
        {tier("standard", standard)}
        {student === null ? null : tier("with_daycare_plan", student)}
      </div>
      {applied === "with_daycare_plan" ? (
        <p className="text-xs text-muted-foreground">{t("priceTier.reason", { pet: petName })}</p>
      ) : null}
    </section>
  );
}
